// Commissions engine — Stax Bill sync, statements, rate card, agents, deal
// terms and adjustments. The engine writes the same period rollups the
// classic Commissions page reads, so both views stay in step.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Cog } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EngineSyncPanel } from "@/components/commissions/EngineSyncPanel";
import { EngineStatements } from "@/components/commissions/EngineStatements";
import { EngineRateCard } from "@/components/commissions/EngineRateCard";
import { EngineAgents } from "@/components/commissions/EngineAgents";
import { EngineDeals } from "@/components/commissions/EngineDeals";
import { EngineAdjustments } from "@/components/commissions/EngineAdjustments";
import { EngineTransfers } from "@/components/commissions/EngineTransfers";
import { useCanManage, isSuperAdmin, apiError } from "@/components/commissions/engineShared";

const TABS = ["sync", "statements", "ratecard", "agents", "deals", "adjustments", "transfers"];

export default function CommissionsEngine() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canManage = useCanManage(user);
  const superAdmin = isSuperAdmin(user);
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "sync";
  const [overview, setOverview] = useState(null);
  const [levels, setLevels] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [{ data: ov }, { data: lv }] = await Promise.all([
        api.get("/commissions/engine/overview"), api.get("/commissions/engine/levels")]);
      setOverview(ov); setLevels(lv.items || []);
    } catch (e) { apiError(e, "Could not load the commissions engine"); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="commissions-engine-page">
      <PageHeader testid="commissions-engine-header" title="Commissions engine"
        titleAccessory={<Cog className="ml-2 h-5 w-5 text-slate-400" />}>
        <Button variant="outline" className="h-9" onClick={() => navigate("/commissions")} data-testid="engine-back">
          <ArrowLeft className="h-4 w-4" /> Commissions report
        </Button>
      </PageHeader>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-4 flex-wrap h-auto" data-testid="engine-tabs">
          <TabsTrigger value="sync" data-testid="engine-tab-sync">Billing Sync</TabsTrigger>
          <TabsTrigger value="statements" data-testid="engine-tab-statements">Commission Ledger</TabsTrigger>
          <TabsTrigger value="ratecard" data-testid="engine-tab-ratecard">Commission rates</TabsTrigger>
          <TabsTrigger value="agents" data-testid="engine-tab-agents">Agents</TabsTrigger>
          <TabsTrigger value="deals" data-testid="engine-tab-deals">Deals</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="engine-tab-adjustments">Adjustments</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="engine-tab-transfers">Domain transfers</TabsTrigger>
        </TabsList>
        <TabsContent value="sync"><EngineSyncPanel overview={overview} reload={reload} canManage={canManage} superAdmin={superAdmin} /></TabsContent>
        <TabsContent value="statements">{overview && <EngineStatements overview={overview} canManage={canManage} initialPeriod={params.get("period")} />}</TabsContent>
        <TabsContent value="ratecard"><EngineRateCard canManage={canManage} reloadOverview={reload} /></TabsContent>
        <TabsContent value="agents"><EngineAgents canManage={canManage} levels={levels} reloadOverview={reload} /></TabsContent>
        <TabsContent value="deals"><EngineDeals canManage={canManage} levels={levels} reloadOverview={reload} /></TabsContent>
        <TabsContent value="adjustments"><EngineAdjustments canManage={canManage} reloadOverview={reload} /></TabsContent>
        <TabsContent value="transfers"><EngineTransfers /></TabsContent>
      </Tabs>
    </div>
  );
}
